/**
 * Weather Service Initialization
 * Configures the RONetworkManager singleton with the web HTTP adapter
 */

import { RONetworkManager, RainOwlsMain } from "rainowls-shared";
import { WebHttpAdapter } from "@/adapters/HttpAdapter.web";

export interface WeatherServiceConfig {
  synopticAPIKey?: string;
  synopticBaseUrl?: string;
  firebaseAPIKey?: string;
  openMeteoBaseUrl?: string;
  openMeteoApiKey?: string;
}

let initialized = false;
const PUBLIC_OPEN_METEO = "https://api.open-meteo.com";
const configuredPrimary = () =>
  import.meta.env.VITE_OPEN_METEO_PRIMARY_BASE_URL ||
  import.meta.env.VITE_OPEN_METEO_BASE_URL ||
  "https://om.rainowls.com";

function endpointConfig(usePrivate: boolean) {
  return {
    openMeteoBaseUrl: usePrivate ? configuredPrimary() : PUBLIC_OPEN_METEO,
    openMeteoFallbackBaseUrl: usePrivate ? PUBLIC_OPEN_METEO : "",
    // Wind follows the same primary/fallback policy as the forecast.
    openMeteoWindBaseUrl: "",
    openMeteoApiKey: import.meta.env.VITE_OPEN_METEO_API_KEY || "",
  };
}

/**
 * Initialize weather services with the web HTTP adapter
 * Should be called once at app startup
 */
export function initializeWeatherServices(
  config: WeatherServiceConfig,
): RainOwlsMain {
  if (!initialized) {
    const httpAdapter = new WebHttpAdapter();

    // Get or create the network manager singleton
    RONetworkManager.getInstance();

    // Configure with web adapter (handles case where singleton was created elsewhere)
    RONetworkManager.configure({
      httpAdapter,
      openMeteoStatusCheckEnabled: false,
      timeout: 10000,
      synopticAPIKey: config.synopticAPIKey,
      synopticBaseUrl: config.synopticBaseUrl,
      firebaseAPIKey: config.firebaseAPIKey,
      openMeteoBaseUrl: config.openMeteoBaseUrl || configuredPrimary(),
      openMeteoApiKey: config.openMeteoApiKey,
      openMeteoFallbackBaseUrl: PUBLIC_OPEN_METEO,
      openMeteoWindBaseUrl: "",
    });

    const service = RainOwlsMain.getInstance();
    // Forecast policy is independent of observed current weather.
    service.dataSourceMode = "openmeteo";
    service.skipSynoptic = false;
    service.nwsAlertsEnabled = false;
    initialized = true;
  }

  return RainOwlsMain.getInstance();
}

/**
 * Get the RainOwlsMain instance
 * Must call initializeWeatherServices first
 */
export function getWeatherService(): RainOwlsMain {
  if (!initialized) {
    throw new Error(
      "Weather services not initialized. Call initializeWeatherServices first.",
    );
  }
  return RainOwlsMain.getInstance();
}

/**
 * Get the network manager instance
 * Must call initializeWeatherServices first
 */
export function getNetworkManager(): RONetworkManager {
  if (!initialized) {
    throw new Error(
      "Weather services not initialized. Call initializeWeatherServices first.",
    );
  }
  return RONetworkManager.getInstance();
}

/**
 * Switch between private (om.rainowls.com) and public (api.open-meteo.com) endpoints
 */
export function setOpenMeteoEndpoint(usePrivate: boolean): void {
  if (!initialized) return;

  RONetworkManager.configure(endpointConfig(usePrivate));
}
